import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import {
  CountdownPanel,
  DurationPicker,
  formatClock,
  useCountdown,
} from '@/features/voting/countdown'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { fromZonedInput, toZonedInput } from '@/lib/datetime'

import { useAdminMutation } from '../api'
import { AdminHeader } from '../AdminLayout'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import {
  I18nField,
  NumberField,
  SelectField,
  SwitchField,
  TextField,
  type I18nValue,
} from '../components/fields'
import { StandingsPanel } from '../components/StandingsPanel'
import { UndoPublication } from '../components/UndoPublication'
import { VoteResultsPanel } from '../components/VoteResultsPanel'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type {
  Candidate,
  CompetitionCategory,
  CompetitionRound,
  I18n,
  VoteCategory,
  VoteCategoryStatus,
} from '../types'

/** Un libellé traduit, avec repli sur la première langue renseignée. */
function labelOf(value: I18n, language: string): string {
  return value[language] || value[Object.keys(value)[0] ?? ''] || '—'
}

/**
 * Les transitions du scrutin, dans l'ordre où on les fait au micro.
 *
 * Une fois publié, un vote ne rouvre plus son urne : aucune transition ne
 * part de `published`. Une publication faite par erreur se retire à part
 * (« Annuler la publication », motif obligatoire), et le vote revient gelé.
 * `publish` n'est proposé que pour un scrutin isolé : les tours d'un concours
 * se publient ensemble, depuis le tableau du concours, après la proclamation.
 */
const TRANSITIONS: Record<VoteCategoryStatus, { action: string; danger?: boolean }[]> = {
  closed: [{ action: 'open' }],
  open: [{ action: 'freeze' }, { action: 'close' }],
  frozen: [{ action: 'publish', danger: true }, { action: 'open' }],
  published: [],
}

/** Les refus du serveur sur le tour, traduits plutôt que montrés en français. */
const ROUND_ERRORS: string[] = ['VOTE_ROUND_REQUIRED', 'VOTE_ROUND_OTHER_CONTEST']

/**
 * Votes : catégories, nommés, conduite du scrutin — et le tableau privé de
 * chaque concours.
 *
 * Au concours, chaque tour est noté à 70 % par la salle (les bulletins, ici),
 * à 30 % par le jury du concours (ses notes de 1 à 10, données depuis le
 * téléphone de chaque juré ; le jury se compose dans « Concours »).
 *
 * Rien n'est public avant deux gestes faits depuis le tableau du concours :
 * proclamer le vainqueur, puis publier les scores. Le dépouillement est
 * consultable avant, ici, par l'équipe — c'est ce qui permet de préparer
 * l'annonce sans la rendre publique.
 */
export function AdminVotingPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event, can } = useAdminContext()
  const timezone = event?.timezone ?? 'Europe/Berlin'

  useEffect(() => {
    document.title = t('admin.section.voting')
  }, [t])

  const categories = useCollection<VoteCategory>({
    key: 'admin-vote-categories',
    path: `/admin/events/${eventId}/vote-categories`,
    eventId,
    related: [['public-vote-categories'], ['admin-readiness', eventId ?? '']],
  })
  const candidates = useCollection<Candidate>({
    key: 'admin-candidates',
    path: `/admin/events/${eventId}/competition/candidates`,
    eventId,
  })
  // Les concours (Miss, Master) : chacun porte son classement cumulé.
  const competitions = useCollection<CompetitionCategory>({
    key: 'admin-competition-categories',
    path: `/admin/events/${eventId}/competition/categories`,
    eventId,
  })
  // Les tours : tenue culturelle, tenue de ville, tenue de soirée. Un scrutin
  // s'y rattache, et c'est ce rattachement qui nomme les colonnes du cumul.
  const rounds = useCollection<CompetitionRound>({
    key: 'admin-competition-rounds',
    path: `/admin/events/${eventId}/competition/rounds`,
    eventId,
  })

  const label = (value: I18n) => labelOf(value, i18n.language)

  // Les refus du rattachement au tour s'affichent dans la langue de l'écran,
  // pas dans le français du serveur.
  const rawError = categories.error
  const error =
    rawError instanceof ApiError && ROUND_ERRORS.includes(rawError.code)
      ? new ApiError({
          code: rawError.code,
          status: rawError.status,
          details: rawError.details,
          requestId: rawError.requestId,
          message: t(`admin.voting.errors.${rawError.code}`),
        })
      : rawError

  return (
    <>
      <AdminHeader
        title={t('admin.section.voting')}
        description={t('admin.voting.description')}
      />

      {/* Le classement d'abord : c'est ce qu'on vient lire le soir J, quand la
          configuration des scrutins est faite depuis longtemps. */}
      {/* Le tableau privé exige le droit de lire les scores avant la salle :
          sans lui, chaque panneau n'afficherait qu'un refus. */}
      {competitions.items.length > 0 && can('voting.result.read') ? (
        <section className="mb-10 space-y-5">
          <div>
            <h2 className="font-display text-heading-lg text-content-primary">
              {t('admin.standings.title')}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-content-secondary">
              {t('admin.standings.intro')}
            </p>
          </div>
          {competitions.items.map((competition) => (
            <StandingsPanel
              key={competition.id}
              eventId={eventId}
              competitionCategoryId={competition.id}
              title={label(competition.name_i18n)}
              can={can}
            />
          ))}
        </section>
      ) : null}

      <CollectionEditor<VoteCategory>
        title={t('admin.voting.categories')}
        description={t('admin.voting.categoriesHint')}
        items={categories.items}
        busy={categories.busy}
        error={error}
        emptyLabel={t('admin.voting.noCategories')}
        createLabel={can('voting.category.create') ? t('admin.voting.addCategory') : null}
        onDelete={(category) => categories.remove.mutate({ id: category.id })}
        // Une catégorie publiée est un résultat annoncé : elle appartient à
        // l'histoire de la soirée. Et sans le droit de supprimer, pas de
        // bouton qui n'aboutirait qu'à un refus.
        canDelete={(category) =>
          can('voting.category.delete') && category.status === 'closed'
        }
        summarize={(category) => ({
          title: label(category.name_i18n),
          subtitle: [
            category.is_tiebreak ? t('admin.voting.tiebreak') : null,
            t('admin.voting.nominees', { count: category.candidate_ids.length }),
            t('admin.voting.quota', { count: category.max_votes_per_attendee }),
            category.requires_checkin ? t('admin.voting.checkinRequired') : null,
            // Le compteur, pas le score : on voit la salle voter sans savoir
            // pour qui — c'est ce qu'on regarde pour décider de geler.
            category.status !== 'closed'
              ? t('admin.voting.ballots', { count: category.ballots })
              : null,
          ]
            .filter(Boolean)
            .join(' · '),
          dimmed: !category.is_active,
          badges: [
            {
              label: t(`admin.voting.status.${category.status}`),
              tone:
                category.status === 'open'
                  ? ('success' as const)
                  : category.status === 'published'
                    ? ('neutral' as const)
                    : ('warning' as const),
            },
          ],
        })}
        renderForm={(category, close) => (
          <CategoryPanel
            category={category}
            candidates={candidates.items}
            competitions={competitions.items}
            rounds={rounds.items}
            takenRounds={
              new Set(
                categories.items
                  .filter((entry) => entry.id !== category?.id && entry.competition_round_id)
                  .map((entry) => entry.competition_round_id as string),
              )
            }
            candidateLabel={(candidate) =>
              [candidate.candidate_number ? `${candidate.candidate_number}.` : null,
                candidate.stage_name ?? `${candidate.first_name} ${candidate.last_name}`]
                .filter(Boolean)
                .join(' ')
            }
            eventId={eventId}
            timezone={timezone}
            can={can}
            busy={categories.busy}
            onCancel={close}
            onSubmit={(payload) => {
              const done = { onSuccess: () => close() }
              if (category) categories.update.mutate({ id: category.id, ...payload }, done)
              else categories.create.mutate(payload, done)
            }}
          />
        )}
      />
    </>
  )
}

function CategoryPanel({
  category,
  candidates,
  competitions,
  rounds,
  takenRounds,
  candidateLabel,
  eventId,
  timezone,
  can,
  busy,
  onSubmit,
  onCancel,
}: {
  category: VoteCategory | null
  candidates: Candidate[]
  competitions: CompetitionCategory[]
  rounds: CompetitionRound[]
  /** Les tours qui ont déjà un AUTRE vote : on ne les propose pas d'office. */
  takenRounds: Set<string>
  candidateLabel: (candidate: Candidate) => string
  eventId: string | undefined
  /** Le fuseau de l'édition : les horaires se saisissent à l'heure de la salle. */
  timezone: string
  can: (permission: string) => boolean
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t, i18n } = useTranslation()
  const [name, setName] = useState<I18nValue>(category?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(category?.description_i18n ?? {})
  const [quota, setQuota] = useState<number | null>(category?.max_votes_per_attendee ?? 1)
  const [allowChange, setAllowChange] = useState(category?.allow_change_vote ?? false)
  const [requiresCheckin, setRequiresCheckin] = useState(category?.requires_checkin ?? true)
  const [active, setActive] = useState(category?.is_active ?? true)
  const [nominees, setNominees] = useState<string[]>(category?.candidate_ids ?? [])
  const [competition, setCompetition] = useState(category?.competition_category_id ?? '')
  const [round, setRound] = useState(category?.competition_round_id ?? '')
  const [roundMissing, setRoundMissing] = useState(false)
  // Un vote de concours est le vote d'un tour : sans tour, il ne compte dans
  // aucun cumul (le serveur le refuse). Seul le départage, créé par le
  // tableau du concours, vit sans tour.
  const isTiebreak = category?.is_tiebreak ?? false
  const contestRounds = rounds.filter((entry) => entry.competition_category_id === competition)
  const roundNeeded = Boolean(competition) && !isTiebreak

  const chooseCompetition = (value: string) => {
    setCompetition(value)
    setRoundMissing(false)
    if (!value) {
      // Sans concours, pas de tour : un lien resté là ne voudrait rien dire.
      setRound('')
      return
    }
    const own = rounds.filter((entry) => entry.competition_category_id === value)
    if (own.some((entry) => entry.id === round)) return
    // On propose d'office le premier tour qui n'a pas encore son vote.
    setRound(own.find((entry) => !takenRounds.has(entry.id))?.id ?? '')
  }
  // Le garde-fou de l'horaire : le vote se ferme seul si personne n'y pense.
  const [opensAt, setOpensAt] = useState(toZonedInput(category?.opens_at ?? null, timezone))
  const [closesAt, setClosesAt] = useState(toZonedInput(category?.closes_at ?? null, timezone))

  // Seuls les candidats approuvés peuvent être nommés : proposer les autres
  // ferait figurer sur un bulletin quelqu'un qui a été refusé. Un finaliste
  // (qualifié au tour précédent) l'est aussi — c'est même lui qu'on nomme aux
  // tours suivants ; le filtre sur `approved` seul le faisait disparaître.
  //
  // Et seulement ceux du concours de ce vote : une candidate Miss cochée dans
  // un tour Master entrait au classement Master (le serveur le refuse
  // désormais). Le concours ENREGISTRÉ fait foi — c'est lui que le serveur
  // vérifie quand on enregistre les nommés.
  const contestOfVote = category?.competition_category_id ?? null
  const eligible = candidates.filter(
    (candidate) =>
      ['approved', 'finalist', 'winner'].includes(candidate.status) &&
      (!contestOfVote || candidate.competition_category_id === contestOfVote),
  )
  const canSave = category ? can('voting.category.update') : can('voting.category.create')
  const canNominate = can('voting.category.update')
  const canControl = can('voting.category.control')
  // Les règles se figent à l'ouverture : on le montre plutôt que de laisser
  // modifier un champ que le serveur refusera.
  const rulesLocked = Boolean(category && category.status !== 'closed')

  // Publier un tour peut rendre un cumul public et faire un vainqueur : le
  // classement affiché en haut de page doit le refléter sans recharger.
  const invalidate = [
    ['admin-vote-categories', eventId],
    ['public-vote-categories'],
    ['admin-standings'],
    ['admin-candidates', eventId],
  ]

  const setNominated = useAdminMutation<VoteCategory, { candidate_ids: string[] }>(
    'put',
    () => `/admin/events/${eventId}/vote-categories/${category?.id}/nominees`,
    eventId,
    invalidate,
  )
  const control = useAdminMutation<
    VoteCategory,
    { action: string; duration_seconds?: number | null }
  >(
    'post',
    (variables) =>
      `/admin/events/${eventId}/vote-categories/${category?.id}/${variables.action}`,
    eventId,
    invalidate,
  )
  const unpublish = useAdminMutation<VoteCategory, { reason: string }>(
    'post',
    () => `/admin/events/${eventId}/vote-categories/${category?.id}/unpublish`,
    eventId,
    invalidate,
  )

  const error = setNominated.error ?? control.error ?? unpublish.error
  // Un nommé d'un autre concours (resté d'avant ce contrôle) n'est plus
  // proposé : on ne l'envoie pas non plus, sinon le serveur refuserait tout.
  const chosenNominees = nominees.filter((id) => eligible.some((entry) => entry.id === id))
  // Un tour de concours (ou son départage) ne se publie pas seul : le serveur
  // le refuserait, on ne propose donc pas le bouton.
  const inContest = Boolean(category?.competition_category_id)
  const transitions = category
    ? TRANSITIONS[category.status].filter(
        (transition) => !(inContest && transition.action === 'publish'),
      )
    : []
  const working = busy || setNominated.isPending || control.isPending || unpublish.isPending
  // Le décompte annoncé à la salle : choisi à l'ouverture, réglé pendant le
  // vote. À zéro, rien ne se ferme — « Geler » passe seulement au premier plan.
  const [duration, setDuration] = useState<number | null>(category?.countdown_seconds ?? null)
  const remaining = useCountdown(
    category?.status === 'open' ? category.countdown_ends_at : null,
    category?.server_now,
  )
  const elapsed = remaining === 0
  const opening = canControl && transitions.some((transition) => transition.action === 'open')

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        {/* Les deux rattachements qui font le cumul. Sans concours, ce
            scrutin vit seul et son score ne s'additionne à rien. */}
        <SelectField
          label={t('admin.voting.competition')}
          hint={t('admin.voting.competitionHint')}
          value={competition}
          onChange={chooseCompetition}
          options={[
            { value: '', label: t('admin.voting.noCompetition') },
            ...competitions.map((entry) => ({
              value: entry.id,
              label: labelOf(entry.name_i18n, i18n.language),
            })),
          ]}
        />
        <div>
          <SelectField
            label={t('admin.voting.round')}
            hint={t('admin.voting.roundHint')}
            value={round}
            onChange={(value) => {
              setRound(value)
              setRoundMissing(false)
            }}
            // Sans concours, un tour n'a pas de sens : le champ reste figé
            // sur « Aucun tour ».
            disabled={!competition}
            options={[
              // « Aucun tour » n'est un choix que hors concours (ou pour un
              // départage). Dans un concours, l'option vide n'est qu'une
              // invite à choisir.
              ...(roundNeeded
                ? round
                  ? []
                  : [{ value: '', label: t('admin.voting.chooseRound') }]
                : [{ value: '', label: t('admin.voting.noRound') }]),
              ...contestRounds.map((entry) => ({
                value: entry.id,
                label: labelOf(entry.name_i18n, i18n.language),
              })),
            ]}
          />
          {roundNeeded && contestRounds.length === 0 ? (
            <p className="mt-1 text-xs text-state-warning">
              {t('admin.voting.noRoundsInContest')}
            </p>
          ) : roundNeeded && roundMissing && !round ? (
            <p className="mt-1 text-xs text-state-danger">{t('admin.voting.roundRequired')}</p>
          ) : null}
        </div>

        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <NumberField
          label={t('admin.voting.maxVotes')}
          hint={t('admin.voting.maxVotesHint')}
          value={quota}
          min={1}
          max={50}
          onChange={setQuota}
          disabled={rulesLocked}
        />
        <SwitchField
          label={t('admin.voting.allowChange')}
          hint={t('admin.voting.allowChangeHint')}
          value={allowChange}
          onChange={setAllowChange}
          disabled={rulesLocked}
        />
        <SwitchField
          label={t('admin.voting.requiresCheckin')}
          hint={t('admin.voting.requiresCheckinHint')}
          value={requiresCheckin}
          onChange={setRequiresCheckin}
          disabled={rulesLocked}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <TextField
          label={t('admin.voting.opensAt')}
          hint={t('admin.voting.windowHint', { timezone })}
          type="datetime-local"
          value={opensAt}
          onChange={setOpensAt}
        />
        <TextField
          label={t('admin.voting.closesAt')}
          hint={t('admin.voting.windowHint', { timezone })}
          type="datetime-local"
          value={closesAt}
          onChange={setClosesAt}
        />
      </div>
      {rulesLocked ? (
        <p className="mt-3 text-xs text-state-warning">{t('admin.voting.rulesLocked')}</p>
      ) : null}

      {canSave ? (
        <FormActions
          busy={working}
          onCancel={onCancel}
          onSubmit={() => {
            if (roundNeeded && !round) {
              setRoundMissing(true)
              return
            }
            onSubmit({
              name_i18n: name,
              description_i18n: description,
              competition_category_id: competition || null,
              competition_round_id: (competition && round) || null,
              max_votes_per_attendee: quota ?? 1,
              allow_change_vote: allowChange,
              requires_checkin: requiresCheckin,
              is_active: active,
              opens_at: fromZonedInput(opensAt, timezone),
              closes_at: fromZonedInput(closesAt, timezone),
            })
          }}
        />
      ) : (
        <p className="mt-5 text-caption text-content-muted">{t('admin.readOnly')}</p>
      )}

      {/* Nommés et conduite : réservés à une catégorie déjà créée. */}
      {category ? (
        <>
          <div className="mt-8 border-t border-line-subtle pt-6">
            <p className="text-caption uppercase tracking-wide text-content-muted">
              {t('admin.voting.nomineesLabel')}
            </p>
            <p className="mt-1 text-xs text-content-muted">{t('admin.voting.nomineesHint')}</p>

            {competition !== (contestOfVote ?? '') ? (
              <p className="mt-2 text-xs text-state-warning">
                {t('admin.voting.nomineesAfterSave')}
              </p>
            ) : null}
            {eligible.length === 0 ? (
              <p className="mt-3 text-sm text-state-warning">
                {t('admin.voting.noApproved')}
              </p>
            ) : (
              <>
                <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
                  {eligible.map((candidate) => {
                    const chosen = nominees.includes(candidate.id)
                    return (
                      <li key={candidate.id}>
                        <button
                          type="button"
                          disabled={!canNominate}
                          onClick={() =>
                            setNominees((current) =>
                              chosen
                                ? current.filter((id) => id !== candidate.id)
                                : [...current, candidate.id],
                            )
                          }
                          aria-pressed={chosen}
                          className={cn(
                            'w-full rounded-md border px-3 py-2 text-left text-sm transition',
                            chosen
                              ? 'border-champagne-400/50 bg-bg-overlay text-content-primary'
                              : 'border-line-subtle text-content-secondary hover:text-content-primary',
                          )}
                        >
                          {candidateLabel(candidate)}
                        </button>
                      </li>
                    )
                  })}
                </ul>

                {canNominate ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    disabled={working || chosenNominees.length === 0}
                    onClick={() => setNominated.mutate({ candidate_ids: chosenNominees })}
                  >
                    {t('admin.voting.saveNominees')}
                  </Button>
                ) : null}
              </>
            )}
          </div>

          <div className="mt-8 border-t border-line-subtle pt-6">
            <p className="text-caption uppercase tracking-wide text-content-muted">
              {t('admin.voting.conduct')}
            </p>
            <p className="mt-1 text-xs text-content-muted">
              {t('admin.voting.currentStatus', {
                status: t(`admin.voting.status.${category.status}`),
              })}
            </p>

            {category.status === 'open' ? (
              <CountdownPanel
                eventId={eventId}
                vote={category}
                remaining={remaining}
                canControl={canControl}
              />
            ) : null}
            {opening ? (
              <div className="mt-3">
                <DurationPicker value={duration} onChange={setDuration} disabled={working} />
              </div>
            ) : null}

            <div className="mt-3 flex flex-wrap gap-2">
              {(canControl ? transitions : []).map((transition) => (
                <Button
                  key={transition.action}
                  size="sm"
                  variant={
                    transition.danger || (elapsed && transition.action === 'freeze')
                      ? 'ember'
                      : 'outline'
                  }
                  disabled={working}
                  onClick={() =>
                    control.mutate(
                      transition.action === 'open' && duration !== null
                        ? { action: 'open', duration_seconds: duration }
                        : { action: transition.action },
                    )
                  }
                >
                  {transition.action === 'open' && duration !== null
                    ? t('voteTimer.openFor', { time: formatClock(duration) })
                    : t(`admin.voting.action.${transition.action}`)}
                </Button>
              ))}
              {transitions.length === 0 ? (
                <p className="text-sm text-content-muted">{t('admin.voting.published')}</p>
              ) : !canControl ? (
                <p className="text-sm text-content-muted">{t('admin.voting.noControlRight')}</p>
              ) : null}
            </div>

            {category.status === 'frozen' && !inContest ? (
              <p className="mt-3 text-xs text-state-warning">{t('admin.voting.publishWarning')}</p>
            ) : null}
            {/* Publié par erreur : on retire, avec un motif. Un tour de
                concours se retire avec tout son concours, depuis son tableau. */}
            {category.status === 'published' && !inContest && canControl ? (
              <UndoPublication
                className="mt-3"
                label={t('admin.undo.voteUnpublish')}
                confirmLabel={t('admin.undo.voteUnpublishConfirm')}
                warnings={[t('admin.undo.voteUnpublishWarning'), t('admin.undo.noMail')]}
                busy={unpublish.isPending}
                disabled={working}
                onConfirm={(reason) => unpublish.mutateAsync({ reason })}
              />
            ) : null}
            {category.status === 'published' && inContest ? (
              <p className="mt-3 text-xs text-content-muted">
                {t('admin.undo.contestFromStandings')}
              </p>
            ) : null}
            {inContest && category.status !== 'published' ? (
              <p className="mt-3 text-xs text-content-muted">{t('admin.voting.contestPublish')}</p>
            ) : null}
          </div>

          <VoteResultsPanel
            eventId={eventId}
            can={can}
            categoryId={category.id}
            nameOf={(candidateId) => {
              const candidate = candidates.find((entry) => entry.id === candidateId)
              return candidate ? candidateLabel(candidate) : '—'
            }}
          />
        </>
      ) : null}

      {error instanceof ApiError ? (
        <p className="mt-4 text-sm text-state-danger">{error.message}</p>
      ) : null}
    </div>
  )
}
